import React from "react";
import { useTranslation } from "react-i18next";
import { useServiceData } from "../../context/ServiceContext";
import { validateUserAgreement, registerUser } from "../../api/apiService";
import { ValidationForm } from "../Validation/ValidationForm";
import { SubcriptionForm } from "../Subcription/SubcriptionForm";
import { Button, Modal } from "../../components";
import { MODAL_TYPES } from "../../utils/modal.utils";
import type { FormikHelpers } from "formik";
import type {
  SubcriptionFormValues,
  SubcriptionPayload,
  ValidationFormValues,
} from "../../types/form.types";
import styles from "./Form.module.css";
import { AxiosError } from "axios";

type ReCaptcha = string | null;

export const FormContainer = () => {
  const { t } = useTranslation();
  const { documentTypes, hobbies, departments, cities } = useServiceData();

  const [validationData, setValidationData] =
    React.useState<ValidationFormValues>();
  const [isValidated, setIsValidated] = React.useState<boolean>(false);
  const [activeModal, setActiveModal] = React.useState<
    keyof typeof MODAL_TYPES | null
  >(null);

  const [initialReCaptcha, setInitialRechaptcha] =
    React.useState<ReCaptcha>(null);
  const [subcriptionReCaptcha, setSubcriptionRechaptcha] =
    React.useState<ReCaptcha>(null);

  const closeModal = () => setActiveModal(null);

  const renderModal = React.useMemo(() => {
    if (!activeModal) return null;
    const { title, icon, message } = MODAL_TYPES[activeModal];

    return (
      <Modal
        isOpen={!!activeModal}
        onClose={closeModal}
        title={t(title)}
        icon={icon}
      >
        <div className={styles.textCenter}>
          <p className={styles.textMuted}>{t(message)}</p>
          <Button type="button" onClick={closeModal}>
            {t("modal.button")}
          </Button>
        </div>
      </Modal>
    );
  }, [t, activeModal]);

  const handleValidation = async (values: ValidationFormValues) => {
    if (!initialReCaptcha) {
      alert(t("form.validation.recaptcha"));
      return;
    }

    try {
      const { data } = await validateUserAgreement(values);
      if (/^true$/.test(data.code)) {
        setIsValidated(true);
        setValidationData(values);
        return;
      }

      setIsValidated(false);
      setActiveModal("invalid");
    } catch (error) {
      console.error(error);
      setActiveModal("failure");
    }
  };

  const handleSubmitData = async (
    values: SubcriptionFormValues,
    { setSubmitting, resetForm }: FormikHelpers<SubcriptionFormValues>
  ) => {
    if (!subcriptionReCaptcha) {
      alert(t("form.validation.recaptcha"));
      return;
    }

    try {
      const payload: SubcriptionPayload = {
        documentNumber: validationData?.documentNumber,
        documentType: documentTypes.find(
          (d) => d.code === validationData?.documentType
        ),
        hobbies: hobbies.filter((h) => values.hobbies.includes(h.value)),
        email: values.email,
        phone: values.phone,
        department: departments.find((d) => d.id === values.department),
        city: cities.find((c) => c.code === values.city),
        address: values.address,
        acceptTermsAndConditions: values.acceptTermsAndConditions,
      };

      const { data } = await registerUser(payload);
      setActiveModal(data.code === "200" ? "success" : "failure");
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error(axiosError);
      if (axiosError.response && axiosError.response.status === 409) {
        setActiveModal("registered");
      } else {
        setActiveModal("failure");
      }
    } finally {
      setSubmitting(false);
      setIsValidated(false);
      resetForm();
    }
  };

  return (
    <div className={styles.formContainer}>
      <h2 className={styles.formTitle}>{t("form.title")}</h2>
      <p className={styles.formSubtitle}>{t("form.subtitle")}</p>
      <ValidationForm
        wasValidated={isValidated}
        onValidate={handleValidation}
        initialReCaptcha={initialReCaptcha}
        onRecaptchaChange={setInitialRechaptcha}
      />
      {isValidated && (
        <SubcriptionForm
          onSubmit={handleSubmitData}
          subcriptionReCaptcha={subcriptionReCaptcha}
          onRecaptchaChange={setSubcriptionRechaptcha}
        />
      )}
      {renderModal}
    </div>
  );
};
